// Type Curve · TC-2 — Merged type-curve + P10/P90 uncertainty HERO chart.
//
// Spec: docs/domain/review_type_curve_plan.md §3 P0 · TC-2
//   Collapse the (previously separate) "Type Curve" chart and "Uncertainty"
//   chart into ONE canvas that overlays, on a single time axis:
//     * the normalized well series (spaghetti, light grey, thin) —
//     * the P50 type well (bold),
//     * the P10 / P90 band (shaded ribbon),
//     * plus the recommended DCA fit and the user DCA fit (retained lines).
//
//   The band drawing mirrors the analyzer's DeclineHeroChart idiom: two
//   envelope lines (lower + stacked thickness) with an areaStyle fill, phase
//   coloured and low-alpha, pushed UNDER the well / P50 / fit lines.
//
// Boundary (PM): this component is a VIEW ONLY. It consumes data already
// produced by the existing endpoints — it does NOT re-normalize (/1000 ft) and
// does NOT re-fit the decline. Specifically:
//   * `performance`  (service getPerformance) → { history:[[date,val]…],
//       dca:{ fitted:[[date,val]…]  /* P50 type well */,
//             lower:[[date,val]…]   /* P90 envelope  */,
//             upper:[[date,val]…]   /* P10 envelope  */ } }
//   * `recomm_data`  = recommended-DCA fitted line  (getDcaRecommends.dca.fitted)
//   * `user_dca`     = user-tuned DCA fitted line    (getDcaUserTuneData.dca.fitted)
//
// NOTE on "spaghetti": the current backend only returns the normalized *average*
// production (`performance.history`) — there is no per-well normalized series
// exposed yet. So the light-grey "spaghetti" layer here is that normalized
// average history (the only normalized well data available), plotted as faint
// points + a thin line. True per-well spaghetti needs a new backend field and is
// out of scope for this P0 view pass (noted in the delivery report).
import { useMemo, useCallback, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Empty } from "antd";

// Phase → petroleum-standard colours (oil green / gas red / water-liquid blue),
// aligned with DeclineHeroChart / log_viewer_defaults.
const PHASE_COLOR = { oil: "#2E8B57", gas: "#FF6B35", liquid: "#4169E1" };

// Log axis cannot show <= 0; clamp to a tiny positive floor so a zero-rate
// month does not blank the whole log plot.
const LOG_FLOOR = 1e-4;
const clampLog = (v) => (v == null ? null : v <= 0 ? LOG_FLOOR : v);

const fmtDate = (v) => {
  if (v == null) return v;
  if (!isNaN(v)) return v;
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${("0" + (d.getMonth() + 1)).slice(-2)}/${("0" + d.getDate()).slice(
    -2
  )}/${d.getFullYear()}`;
};

/**
 * props:
 *   performance : { history, dca:{ fitted, lower, upper } } | null
 *   recommData  : [[date,val]…] | null  (recommended DCA fit)
 *   userDca     : [[date,val]…] | null  (user-tuned DCA fit)
 *   declinePhase: "oil" | "gas" | "liquid"
 *   yUnit       : string
 */
const TypeCurveHeroChart = (props) => {
  const { performance, recommData, userDca, declinePhase, yUnit } = props;
  // TC-2 keeps the semi-log ⇄ linear toggle that the old charts implied via
  // ECharts value axis; log is the sensible default for a decline curve.
  const [yScale, setYScale] = useState("log");
  const color = PHASE_COLOR[declinePhase] || "#2E8B57";

  const history = performance?.history || null;
  const p50 = performance?.dca?.fitted || null; // P50 type well
  const p90 = performance?.dca?.lower || null; // lower envelope
  const p10 = performance?.dca?.upper || null; // upper envelope

  const hasAny =
    (history && history.length) ||
    (recommData && recommData.length) ||
    (userDca && userDca.length);

  const option = useMemo(() => {
    if (!hasAny) return null;
    const log = yScale === "log";

    // --- assemble a single, ordered category x-axis over every series' dates.
    const dateSet = new Set();
    const collect = (arr) =>
      (arr || []).forEach((it) => it && dateSet.add(it[0]));
    collect(history);
    collect(p50);
    collect(p90);
    collect(p10);
    collect(recommData);
    collect(userDca);
    // Dates come as ISO strings / day indices; a lexical sort keeps ISO in
    // chronological order and numeric-day strings ascending too.
    const xAxis = Array.from(dateSet).sort((a, b) =>
      String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0
    );
    const xIndex = new Map();
    xAxis.forEach((d, i) => xIndex.set(d, i));

    // Project a [[date,val]…] series onto the shared axis (null-padded).
    const project = (arr, transform) => {
      const out = new Array(xAxis.length).fill(null);
      (arr || []).forEach((it) => {
        if (!it) return;
        const idx = xIndex.get(it[0]);
        if (idx == null) return;
        const v = it[1];
        out[idx] = transform ? transform(v) : v;
      });
      return out;
    };
    const asY = (v) => (v == null ? null : log ? clampLog(v) : v);

    const histVals = project(history, asY);
    const p50Vals = project(p50, asY);
    const recommVals = project(recommData, asY);
    const userVals = project(userDca, asY);

    // --- P10/P90 band (DeclineHeroChart idiom): lower envelope as an invisible
    // base line, then STACK the (p10 − p90) thickness with an areaStyle fill so
    // the ribbon lands exactly between the two envelopes. Guard thickness >= 0.
    let bandLower = null;
    let bandThick = null;
    if (p90 && p10 && p90.length && p10.length) {
      const lower = new Array(xAxis.length).fill(null);
      const thick = new Array(xAxis.length).fill(null);
      const loByDate = new Map((p90 || []).map((it) => [it[0], it[1]]));
      const hiByDate = new Map((p10 || []).map((it) => [it[0], it[1]]));
      xAxis.forEach((d, i) => {
        const loRaw = loByDate.get(d);
        const hiRaw = hiByDate.get(d);
        if (loRaw == null && hiRaw == null) return;
        const lo = asY(loRaw);
        const hi = asY(hiRaw);
        if (lo != null) lower[i] = lo;
        if (lo != null && hi != null) {
          thick[i] = log
            ? Math.max(hi, lo) - Math.min(hi, lo)
            : Math.max(0, hi - lo);
        }
      });
      bandLower = lower;
      bandThick = thick;
    }

    const series = [];

    // Band fill first (z low) so it renders under everything.
    if (bandLower) {
      series.push({
        name: "P90 (lower)",
        type: "line",
        data: bandLower,
        stack: "p10p90band",
        showSymbol: false,
        symbol: "none",
        lineStyle: { opacity: 0 },
        itemStyle: { color },
        tooltip: { show: false },
        silent: true,
        connectNulls: true,
        z: 1,
      });
      series.push({
        name: "P10–P90 band",
        type: "line",
        data: bandThick,
        stack: "p10p90band",
        showSymbol: false,
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { color, opacity: 0.16 },
        itemStyle: { color },
        tooltip: { show: false },
        silent: true,
        connectNulls: true,
        z: 1,
      });
    }

    // Normalized well "spaghetti" proxy: faint grey points + a thin line.
    if (history && history.length) {
      series.push({
        name: "Normalized wells",
        type: "line",
        data: histVals,
        showSymbol: true,
        symbol: "circle",
        symbolSize: 3,
        lineStyle: { color: "#9aa0b5", width: 1, opacity: 0.5 },
        itemStyle: { color: "#9aa0b5", opacity: 0.55 },
        connectNulls: false,
        z: 2,
      });
    }

    // P50 type well (bold).
    if (p50 && p50.length) {
      series.push({
        name: "P50 type well",
        type: "line",
        data: p50Vals,
        showSymbol: false,
        smooth: false,
        lineStyle: { color, width: 3 },
        itemStyle: { color },
        connectNulls: true,
        z: 5,
      });
    }

    // Recommended DCA fit.
    if (recommData && recommData.length) {
      series.push({
        name: "Auto-regression",
        type: "line",
        data: recommVals,
        showSymbol: false,
        lineStyle: { color: "#fac858", width: 2 },
        itemStyle: { color: "#fac858" },
        connectNulls: true,
        z: 4,
      });
    }

    // User DCA fit.
    if (userDca && userDca.length) {
      series.push({
        name: "User-Arp's",
        type: "line",
        data: userVals,
        showSymbol: false,
        lineStyle: { color: "#ee6666", width: 2, type: "dashed" },
        itemStyle: { color: "#ee6666" },
        connectNulls: true,
        z: 4,
      });
    }

    const legendData = series
      .map((s) => s.name)
      .filter((n) => n && n !== "P90 (lower)");

    return {
      backgroundColor: "transparent",
      grid: { left: "4%", right: "4%", top: 44, bottom: 44, containLabel: true },
      legend: { top: 8, textStyle: { color: "#ccc" }, data: legendData },
      tooltip: {
        trigger: "axis",
        axisPointer: { snap: true, label: { precision: 3 } },
      },
      toolbox: {
        right: 10,
        top: 4,
        itemSize: 16,
        feature: {
          dataZoom: { yAxisIndex: "none" },
          restore: {},
          saveAsImage: { type: "png" },
        },
      },
      dataZoom: [{ type: "inside" }],
      xAxis: {
        type: "category",
        data: xAxis,
        boundaryGap: false,
        axisLabel: { color: "#aaa", formatter: fmtDate },
      },
      yAxis: {
        type: log ? "log" : "value",
        name: yUnit || "",
        nameTextStyle: { color: "#aaa" },
        min: log ? LOG_FLOOR : undefined,
        axisLabel: { color: "#aaa" },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      series,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, p50, p90, p10, recommData, userDca, yScale, color, yUnit, hasAny]);

  const toggleScale = useCallback(
    () => setYScale((s) => (s === "log" ? "value" : "log")),
    []
  );

  return (
    <div className="d-flex flex-column h-100" style={{ position: "relative" }}>
      <div className="d-flex flex-row justify-content-end align-items-center mb-1">
        <span
          role="button"
          onClick={toggleScale}
          className="btn-custom"
          title="Toggle linear / semi-log Y axis"
          style={{ cursor: "pointer" }}
        >
          Y: {yScale === "log" ? "Log" : "Linear"}
        </span>
      </div>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        {hasAny ? (
          <ReactECharts
            option={option}
            notMerge={true}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: "rgba(255,255,255,0.6)", paddingTop: 60 }}
          >
            Select wells and run clustering / grouping to build a type curve.
          </Empty>
        )}
      </div>
    </div>
  );
};

export default TypeCurveHeroChart;
